// Client-side presence roster store (BL-410 AC 4).
//
// WHY NOT SnapshotContext OR EntityStateStore. Presence looks like a snapshot —
// one whole value per topic, ordered by a monotonic cursor — but it differs on
// the thing those abstractions are built around: there is no durable store
// behind it and therefore NO CATCH-UP ROUTE. A snapshot's correctness comes from
// the catch-up endpoint owning cold-seed and gap recovery (ADR 011 §5); a
// presence roster lives only in Redis and only while sockets are open. The
// gateway announces the current roster to a joining reader instead, which is the
// cold-seed, and every frame is the whole truth, which is the gap recovery.
// `EntityStateStore` is a per-entity LWW fold keyed on an id — presence has no
// per-entity identity to fold on, only a set. So this is a small dedicated store
// rather than a forced binding of either.
//
// WHOLE-SET REPLACE. Each frame carries the complete roster, so a dropped frame
// self-heals on the next one with no gap detection. The only ordering concern is
// a stale frame arriving late, which `at` handles.

import type { RealtimeSubscriber } from '../../types/realtime/subscriber';
import type { Unsubscribe } from '../../types/realtime/types';

/** Stable empty set, so an unheld topic reads referentially stable. */
const EMPTY: ReadonlySet<string> = new Set<string>();

/** One topic's roster and the cursor it was observed at. */
interface RosterEntry {
    online: ReadonlySet<string>;
    at: number;
    listeners: Set<() => void>;
    dispose: Unsubscribe;
}

/**
 * Holds the online roster for every presence topic this tab is watching.
 *
 * Ref-counted per topic: several components legitimately want one auction's
 * roster (the rail and its header count), and they share one subscription.
 */
export class PresenceStore {
    private readonly byTopic = new Map<string, RosterEntry>();

    constructor(private readonly subscriber: RealtimeSubscriber) {}

    /**
     * The `customer_user.id`s currently online for `topic`.
     *
     * Referentially stable while unchanged — consumers go through
     * `useSyncExternalStore`, which re-renders on identity change, so returning
     * a fresh Set per read would re-render every consumer on every frame.
     *
     * @param topic - The presence topic.
     * @returns The online set; empty when nothing is held for this topic.
     */
    get(topic: string): ReadonlySet<string> {
        return this.byTopic.get(topic)?.online ?? EMPTY;
    }

    /**
     * Watch `topic`, taking (or sharing) its subscription.
     *
     * @param topic - The presence topic. Empty string is inert, so a component
     *   with no auction selected yet needs no conditional hook call.
     * @param listener - Called whenever the roster for `topic` changes.
     * @returns Disposer. Idempotent; releases the subscription when it is the last.
     * @sideEffect Subscribes to the realtime transport on first hold.
     */
    subscribe(topic: string, listener: () => void): Unsubscribe {
        if (!topic) return () => {};

        let entry = this.byTopic.get(topic);
        if (entry == null) {
            const created: RosterEntry = {
                online: EMPTY,
                // Below any real epoch-ms cursor, so the first frame always wins.
                at: Number.NEGATIVE_INFINITY,
                listeners: new Set(),
                dispose: () => {},
            };
            this.byTopic.set(topic, created);
            created.dispose = this.subscriber.subscribe({
                topic,
                event: 'presence.updated',
                handler: (payload) => this.apply(topic, payload),
            } as Parameters<RealtimeSubscriber['subscribe']>[0]);
            entry = created;
        }
        entry.listeners.add(listener);

        let disposed = false;
        return () => {
            if (disposed) return;
            disposed = true;
            const held = this.byTopic.get(topic);
            if (!held) return;
            held.listeners.delete(listener);
            if (held.listeners.size > 0) return;
            held.dispose();
            // Drop the roster too: a clerk who moves between auctions all day
            // would otherwise accumulate one per auction they ever opened.
            this.byTopic.delete(topic);
        };
    }

    /**
     * Fold one frame in.
     *
     * Last-writer-wins on `at`. Any gateway instance may publish and each does
     * read-then-publish, so a stale read can land after a fresher one; without
     * this the stale frame would win and persist until the next roster change.
     * Equal cursors are ACCEPTED — two reads observing the same instant are
     * equally true, and refusing them would drop a real update whenever two
     * changes land inside one clock tick.
     */
    private apply(topic: string, payload: unknown): void {
        const entry = this.byTopic.get(topic);
        if (!entry) return;

        const frame = payload as { customerUserIds?: unknown; at?: unknown };
        if (!Array.isArray(frame.customerUserIds) || typeof frame.at !== 'number') return;
        if (frame.at < entry.at) return;

        entry.online = new Set(frame.customerUserIds.filter((id): id is string => typeof id === 'string'));
        entry.at = frame.at;
        // Copied before iterating, deliberately: a listener may dispose itself in
        // response (a component unmounting on the same frame), and mutating the
        // Set mid-iteration would skip its neighbour. The allocation is per
        // roster CHANGE with 1–2 listeners per topic — not worth trading for the
        // bug it prevents.
        for (const l of [...entry.listeners]) l();
    }
}
